
export class LibrarySearchable {
    kind: LibraryItemKind
    tags: string[]
    name: string
    payloadIdx: number
    // rawPayload: any // This will be wither PublicSound or PublicScene

    private constructor(kind: LibraryItemKind, name: string, tags: string[], payloadIdx: number) {
        this.kind = kind;
        this.tags = tags;
        this.name = name;
        this.payloadIdx = payloadIdx;
    }

    clone(): LibrarySearchable {
        return new LibrarySearchable(
            this.kind,
            this.name,
            this.tags,
            this.payloadIdx,
        )
    }

    // This is a publicSound
    public static fromRawSound(rawSound: any, payloadIdx: number): LibrarySearchable {
        return new LibrarySearchable(
            LibraryItemKinds.Sound,
            rawSound.Name,
            rawSound.Tags,
            payloadIdx,
        )
    }

    // This is a publicScene
    public static fromRawScene(rawScene: any, payloadIdx: number): LibrarySearchable {
        return new LibrarySearchable(
            LibraryItemKinds.Scene,
            rawScene.Name,
            rawScene.Tags,
            payloadIdx,
        )
    }
}

type LibraryContentsOperator = (contents: LibrarySearchable[]) => void;
export class LibrarySearchIndex {
    contents: LibrarySearchable[]
    rawSounds: any[];
    rawScenes: any[];

    constructor(searchables: LibrarySearchable[]) {
        this.contents = searchables;
    }

    public static empty(): LibrarySearchIndex {
        return new LibrarySearchIndex([]);
    }

    loadRawSounds(rawSounds: any[]) {
        // There is no relevant type information here
        this.rawSounds = structuredClone(rawSounds);
        const searchables = rawSounds.map((sound, idx) => LibrarySearchable.fromRawSound(sound, idx));
        this.contents.concat(searchables);
    }

    loadRawScenes(rawScenes: any[]) {
        // There is no relevant type information here
        this.rawScenes = structuredClone(rawScenes);
        const searchables = rawScenes.map((sound, idx) => LibrarySearchable.fromRawScene(sound, idx));
        this.contents.concat(searchables);
    }
}

export type LibraryItemKind = string;
export const LibraryItemKinds = {
    Scene: "Scene",
    Sound: "Sound",
}

export type LibraryTransformKind = string;
export const LibraryTransformKinds = {
    ItemKindFilter: "ItemKindFilter",
    ItemTagsFilter: "ItemTagsFilter",
}

function LibraryTransformFromRaw(rawData: { transformKind: LibraryTransformKind }): LibraryTransform | null {
    if (rawData.transformKind === LibraryTransformKinds.ItemKindFilter) {
        return LibraryItemKindFilter.fromRaw(rawData);
    }
    return null;
}

export interface LibraryTransform {
    transformKind: LibraryTransformKind // We need this to be able to deserialize from JSON
    operator: LibraryContentsOperator;
}

export class LibraryItemKindFilter implements LibraryTransform {
    transformKind: LibraryTransformKind = LibraryTransformKinds.ItemKindFilter
    acceptedKinds: LibraryItemKind[]

    constructor(acceptedKinds: LibraryItemKind[]) {
        this.transformKind = LibraryTransformKinds.ItemKindFilter
        this.acceptedKinds = acceptedKinds;
    }

    public static fromRaw(rawData: any) {
        return new LibraryItemKindFilter(
            rawData.acceptedKinds
        )
    }

    operator(contents: LibrarySearchable[]) {
        contents = contents.filter((elem) => elem.kind in this.acceptedKinds)
    }
}

export class LibraryItemTagsFilter implements LibraryTransform {
    transformKind: LibraryTransformKind = LibraryTransformKinds.ItemTagsFilter
    acceptedTag: string

    constructor(tag: string) {
        this.transformKind = LibraryTransformKinds.ItemTagsFilter
        this.acceptedTag = tag;
    }

    public static fromRaw(rawData: any) {
        return new LibraryItemKindFilter(
            rawData.acceptedTag
        )
    }

    operator(contents: LibrarySearchable[]) {
        contents = contents.filter((elem) => this.acceptedTag in elem.tags)
    }
}

export class LibraryQuery {
    name: string | null;
    private transforms: LibraryTransform[]

    private constructor(name: string, transforms: LibraryTransform[] = []) {
        this.name = name;
        this.transforms = [];
    }

    public static fromRaw(rawData: any) {
        return new LibraryQuery(
            rawData.name,
            rawData.transforms.map(LibraryTransformFromRaw)
        )
    }

    run(contents: LibrarySearchIndex): LibrarySearchIndex {
        const searchablesCopy: LibrarySearchable[] = Array.from(contents.contents.map(entry => entry.clone()))
        // TODO BL: We can store partial results in memory just to be more performant when we remove tags
        this.transforms.forEach((transform) => {
            transform.operator(searchablesCopy)
        })
        return new LibrarySearchIndex(searchablesCopy);
    }

    clone(): LibraryQuery {
        const clone = structuredClone(this);
        return LibraryQuery.fromRaw(clone);
    }

    public static empty(name: string): LibraryQuery {
        return new LibraryQuery(
            name,
        )
    }

    append(transform: LibraryTransform): LibraryQuery {
        const cloned = this.clone();
        cloned.transforms.push(transform);
        return cloned;
    }

    pop(): LibraryQuery {
        const cloned = this.clone();
        cloned.transforms.pop();
        return cloned;
    }

    withName(newName: string): LibraryQuery {
        const cloned = this.clone();
        cloned.name = newName;
        return cloned;
    }
}

const SOUNDS_FILTER = new LibraryItemKindFilter([LibraryItemKinds.Sound]);

export const WELL_KNOWN_LIBRARY_QUERIES = {
    All: () => LibraryQuery.empty("All").append(SOUNDS_FILTER),
    Music: () => LibraryQuery.empty("Music").append(SOUNDS_FILTER).append(new LibraryItemTagsFilter("Music")),
    Ambience: () => LibraryQuery.empty("Ambience").append(SOUNDS_FILTER).append(new LibraryItemTagsFilter("Ambience")),
    // SoundEffects: () => new LibraryQuery("Sound Effects", [new LibraryItemKindFilter([LibraryItemKinds.Sounds]), new LibraryItemTagsFilter("Sound Effects")]),
    Scenes: () => LibraryQuery.empty("Scenes").append(new LibraryItemKindFilter([LibraryItemKinds.Scene])),
}